
import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputBase, InputLabel, List, ListItem, ListItemText, MenuItem, Paper, Select, Typography } from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';

import { openSnackbar } from '../store/slices/SnackbarSlice';

import { InputSearchButtonsStyle, InputSearchStyle } from '../styles/FormStyle';

import { ObtenerAutoridadesMaterias } from '../connections/catalogos/AutoridadConnection';
import { ObtenerMateria } from '../connections/catalogos/MateriaConnection';
import { BuscarExpediente } from '../connections/recepcion/ExpedienteConnection';
import { AsignarExpedienteAmparo } from '../connections/recepcion/AmparoConnection';

import { AutoridadProps } from '../interfaces/catalogos/AutoridadInterface';
import { MateriaProps } from '../interfaces/catalogos/MateriaInterface';
import { BuscarExpedienteResponse, defaultExpedienteResponse, ExpedienteProps } from '../interfaces/recepcion/ExpedienteInterface';

interface Props {
    openDialog:         boolean;
    idAmparo:           number;
    handleCloseDialog:  () => void;
    idAcuerdo?:         number;
    onGuardar?:         ( expediente: ExpedienteProps ) => void;
}

export const DialogBuscarExpedienteAmparo = ( { openDialog, handleCloseDialog, idAmparo, idAcuerdo, onGuardar }: Props ) => {

    const dispatch = useDispatch();

    const [loading, setLoading] = useState( false );
    const [loadingGuardar, setLoadingGuardar] = useState( false );

    const [materia, setMateria] = useState( 0 );
    const [materiaArray, setMateriaArray] = useState<MateriaProps[]>( [] );

    const [autoridad, setAutoridad] = useState( 0 );
    const [autoridadArray, setAutoridadArray] = useState<AutoridadProps[]>( [] );

    const [numeroExpediente, setNumeroExpediente] = useState( '' );
    const [expediente, setExpediente] = useState<BuscarExpedienteResponse>( defaultExpedienteResponse );

    const handleBuscar = async () => {

        setLoading( true );

        await BuscarExpediente({ numeroExpediente, idAutoridad: autoridad }).then( resp => {

            const { success, message } = resp;

            setTimeout(() => {

                if( success ){
                    setExpediente( resp );
                } 
                else {
                    setExpediente( defaultExpedienteResponse );
                    dispatch( openSnackbar({ message, variant: 'error' }) );
                }

                setLoading( false );

            }, 700);

        });

    }

    const handleGuardar = async () => {

        setLoadingGuardar( true );

        await AsignarExpedienteAmparo({ idAmparo, idExpediente: expediente.idExpediente, idAcuerdo }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    dispatch( openSnackbar({ message, variant: 'success' }) );
                    handleCerrar();

                    onGuardar?.( expediente );

                } 
                else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoadingGuardar( false );

            }, 700);

        });

    }

    const handleCerrar = () => {

        setMateria( 0 );
        setAutoridad( 0 );
        setAutoridadArray( [] );
        setNumeroExpediente( '' );
        setExpediente( defaultExpedienteResponse );
        handleCloseDialog();

    }

    useEffect(() => {

        async function Obtener(){
            await ObtenerMateria().then( resp => setMateriaArray( resp ) );
        }

        Obtener();

    }, []);

    useEffect(() => {

        async function Obtener(){

            setAutoridad( 0 );
            setAutoridadArray( [] );

            await ObtenerAutoridadesMaterias( materia ).then( resp => setAutoridadArray( resp ) );

        }

        if( materia !== 0 ){
            Obtener();
        }

    }, [ materia ]);

    return (

        <Dialog open={ openDialog } maxWidth='md' fullWidth>

            <DialogTitle>Buscar expediente</DialogTitle>

            <DialogContent>

                <Grid container spacing={3} mt={1}>

                    <Grid size={{ xs: 12, md: 6 }}>

                        <FormControl fullWidth>

                            <InputLabel>Materia</InputLabel>

                            <Select
                                label="Materia"
                                value={ materia }
                                onChange={ (e) => setMateria( parseInt( e.target.value.toString() ) ) }
                            >
                                <MenuItem key={0} value={0}>Selecciona una opción</MenuItem>
                                {
                                    materiaArray
                                    .filter( (e) => e.idMateria === 5 || e.idMateria === 6 )
                                    .map( (e) => (
                                        <MenuItem key={ e.idMateria } value={ e.idMateria }>{ e.descripcion }</MenuItem>
                                    ))
                                }
                            </Select>

                        </FormControl>

                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>

                        <FormControl fullWidth>

                            <InputLabel>Autoridad</InputLabel>

                            <Select
                                label="Autoridad"
                                value={ autoridad }
                                onChange={ (e) => setAutoridad( parseInt( e.target.value.toString() ) ) }
                                disabled={ materia === 0 }
                            >
                                <MenuItem key={0} value={0}>Selecciona una opción</MenuItem>
                                {
                                    autoridadArray.map( (e) => (
                                        <MenuItem key={ e.idAutoridad } value={ e.idAutoridad }>{ e.descripcion }</MenuItem>
                                    ))
                                }
                            </Select>

                        </FormControl>

                    </Grid>

                    <Grid size={{ xs: 12 }}>

                        <Paper component="div" sx={ InputSearchStyle }>

                            <InputBase
                                sx={{ ml: 1, flex: 1 }}
                                placeholder="Número de expediente"
                                value={ numeroExpediente }
                                onChange={ (e) => setNumeroExpediente( e.target.value ) }
                                onKeyDown={ (e) => { if( e.key === 'Enter' && autoridad !== 0 && numeroExpediente !== '' ) handleBuscar(); } }
                            />

                            <Button
                                variant='contained'
                                size='small'
                                sx={ InputSearchButtonsStyle }
                                loading={ loading }
                                disabled={ autoridad === 0 || numeroExpediente === '' }
                                onClick={ handleBuscar }
                            >
                                <SearchIcon />
                            </Button>

                        </Paper>

                    </Grid>

                    {
                        expediente.success
                        &&
                            <>

                                <Grid size={{ md: 6, xs: 12 }}>

                                    <List disablePadding>
                                        <ListItem sx={{ py: 0, px: 0 }}>
                                            <ListItemText
                                                primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>Número de expediente:</Typography> }
                                                secondary={
                                                    <Typography variant='subtitle2' fontWeight='bold'>
                                                        { expediente.numeroExpediente }{ expediente.numeroFolio ? ` (F. ${ expediente.numeroFolio })` : '' }
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    </List>

                                </Grid>

                                <Grid size={{ md: 6, xs: 12 }}>

                                    <List disablePadding>
                                        <ListItem sx={{ py: 0, px: 0 }}>
                                            <ListItemText
                                                primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>Tipo de juicio:</Typography> }
                                                secondary={ <Typography variant='subtitle2' fontWeight='bold'>{ expediente.tipoJuicio }</Typography> }
                                            />
                                        </ListItem>
                                    </List>

                                </Grid>

                                {
                                    expediente.actores.length !== 0
                                    &&
                                        <Grid size={{ md: 6, xs: 12 }}>

                                            <List disablePadding>
                                                <ListItem sx={{ py: 0, px: 0 }}>
                                                    <ListItemText
                                                        primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>Actor(es):</Typography> }
                                                        slotProps={{ secondary: { component: 'div' } }}
                                                        secondary={
                                                            <List disablePadding>
                                                                { expediente.actores.map( (actor, i) => (
                                                                    <ListItem key={i} sx={{ py: 0, px: 0 }}>
                                                                        <Typography variant='subtitle2' fontWeight='bold' sx={{ color: 'black' }}>{ actor }</Typography>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        }
                                                    />
                                                </ListItem>
                                            </List>

                                        </Grid>
                                }

                                {
                                    expediente.demandados.length !== 0
                                    &&
                                        <Grid size={{ md: 6, xs: 12 }}>

                                            <List disablePadding>
                                                <ListItem sx={{ py: 0, px: 0 }}>
                                                    <ListItemText
                                                        primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>Demandado(s):</Typography> }
                                                        slotProps={{ secondary: { component: 'div' } }}
                                                        secondary={
                                                            <List disablePadding>
                                                                { expediente.demandados.map( (demandado, i) => (
                                                                    <ListItem key={i} sx={{ py: 0, px: 0 }}>
                                                                        <Typography variant='subtitle2' fontWeight='bold' sx={{ color: 'black' }}>{ demandado }</Typography>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        }
                                                    />
                                                </ListItem>
                                            </List>

                                        </Grid>
                                }

                            </>
                    }

                </Grid>

            </DialogContent>

            <DialogActions>

                <Button color='secondary' disabled={ loadingGuardar } onClick={ handleCerrar }>Cancelar</Button>

                <Button
                    variant='contained'
                    loading={ loadingGuardar }
                    disabled={ !expediente.success }
                    onClick={ handleGuardar }
                >
                    Guardar
                </Button>

            </DialogActions>

        </Dialog>

    )

}
